import { Box, Chip, Stack, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { useMemo } from "react";
import { formatDate, formatMoney, plColor } from "./plConstants";

const moneyCell =
  (colored) =>
  ({ cell }) => {
    const value = cell.getValue();
    if (!colored) return formatMoney(value);
    return <Box sx={{ color: plColor(value), fontWeight: 600 }}>{formatMoney(value)}</Box>;
  };

// Server-controlled detail table: pagination and sorting are driven by the parent and sent to the
// backend. Grouped view shows one row per client, expandable to its entries; flat shows entries.
const ProfitLossDetailTable = ({
  view,
  onViewChange,
  rows,
  rowCount,
  pagination,
  onPaginationChange,
  sorting,
  onSortingChange,
  isLoading,
}) => {
  const isGrouped = view === "GROUPED";

  const flatColumns = useMemo(
    () => [
      { accessorKey: "clientName", header: "Client", size: 170 },
      { accessorKey: "clientIdentifier", header: "ID", size: 110 },
      { accessorKey: "module", header: "Module", size: 130 },
      { accessorKey: "caseOfficer", header: "Case Officer", size: 130 },
      {
        accessorKey: "date",
        header: "Date",
        size: 110,
        Cell: ({ cell }) => formatDate(cell.getValue()),
      },
      { accessorKey: "agreedAmount", header: "Agreed", size: 110, Cell: moneyCell() },
      { accessorKey: "cost", header: "Cost", size: 110, Cell: moneyCell() },
      { accessorKey: "paidAmount", header: "Paid", size: 110, Cell: moneyCell() },
      { accessorKey: "dueAmount", header: "Due", size: 110, Cell: moneyCell() },
      { accessorKey: "profitLoss", header: "Profit / Loss", size: 120, Cell: moneyCell(true) },
      {
        accessorKey: "commissionClaimed",
        header: "Claims",
        size: 130,
        enableSorting: false,
        Cell: ({ row }) => (
          <Stack direction="row" spacing={0.5}>
            {row.original.commissionClaimed && <Chip size="small" color="success" label="Comm" />}
            {row.original.bonusClaimed && <Chip size="small" color="info" label="Bonus" />}
          </Stack>
        ),
      },
    ],
    []
  );

  const groupedColumns = useMemo(
    () => [
      {
        accessorKey: "clientName",
        header: "Client",
        size: 200,
        Cell: ({ row }) => (row.depth === 0 ? row.original.clientName : ""),
      },
      {
        accessorKey: "clientIdentifier",
        header: "ID",
        size: 120,
        Cell: ({ row }) => (row.depth === 0 ? row.original.clientIdentifier : ""),
      },
      {
        id: "moduleOrCount",
        header: "Module",
        size: 150,
        enableSorting: false,
        Cell: ({ row }) =>
          row.depth === 0
            ? `${row.original.entryCount} ${row.original.entryCount === 1 ? "entry" : "entries"}`
            : row.original.module,
      },
      {
        accessorKey: "caseOfficer",
        header: "Case Officer",
        size: 140,
        Cell: ({ row }) => (row.depth === 0 ? row.original.caseOfficer : ""),
      },
      { accessorKey: "agreedAmount", header: "Agreed", size: 120, Cell: moneyCell() },
      { accessorKey: "cost", header: "Cost", size: 120, Cell: moneyCell() },
      { accessorKey: "paidAmount", header: "Paid", size: 120, Cell: moneyCell() },
      { accessorKey: "dueAmount", header: "Due", size: 120, Cell: moneyCell() },
      { accessorKey: "profitLoss", header: "Profit / Loss", size: 130, Cell: moneyCell(true) },
    ],
    []
  );

  const table = useMaterialReactTable({
    columns: isGrouped ? groupedColumns : flatColumns,
    data: rows || [],
    enableExpanding: isGrouped,
    getSubRows: isGrouped ? (row) => row.entries : undefined,
    manualPagination: true,
    manualSorting: true,
    rowCount: rowCount || 0,
    onPaginationChange,
    onSortingChange,
    state: { pagination, sorting, isLoading, showProgressBars: isLoading },
    enableGlobalFilter: false,
    enableColumnFilters: false,
    enableStickyHeader: true,
    muiTableContainerProps: { sx: { maxHeight: "55vh" } },
    initialState: { density: "compact" },
    renderTopToolbarCustomActions: () => (
      <ToggleButtonGroup
        size="small"
        exclusive
        value={view}
        onChange={(_, v) => v && onViewChange(v)}
      >
        <ToggleButton value="GROUPED">Grouped by client</ToggleButton>
        <ToggleButton value="FLAT">All entries</ToggleButton>
      </ToggleButtonGroup>
    ),
  });

  return <MaterialReactTable table={table} />;
};

export default ProfitLossDetailTable;
